import { ReadonlyVec2, vec2 } from 'gl-matrix';

/** What places the checkerboard pattern on one view, in fragment pixels. */
export interface CheckerboardPatternPx {
    /**
     * The corner four tiles meet at, in `gl_FragCoord` pixels (+y up). It
     * is the crosshair, so the pattern follows the crosshair and not the
     * pan or the zoom.
     */
    readonly originPx: ReadonlyVec2;
    /** One tile's edge per axis, in pixels at the view's current zoom. */
    readonly tilePx: ReadonlyVec2;
}

export interface CheckerboardPatternInput {
    /** The crosshair in canvas pixels (+y down), as the view projects it. */
    readonly crosshairCanvasPx: ReadonlyVec2;
    /** The view's framebuffer size in the same pixels. */
    readonly viewSizePx: ReadonlyVec2;
    /** Patient mm per pixel along each view axis at the current zoom. */
    readonly mmPerPx: ReadonlyVec2;
    /** One tile's edge on the image plane, in mm; a positive length, as the Environment holds it. */
    readonly tileSizeMm: number;
}

/**
 * Places a checkerboard of `tileSizeMm` tiles on a view so that four tiles
 * meet at the crosshair. The tile is a length on the image plane, so it is
 * converted to pixels per axis at the view's zoom; the canvas y axis is
 * flipped into fragment space, which grows upward from the framebuffer's
 * bottom edge.
 */
export function checkerboardPatternPx({
    crosshairCanvasPx,
    viewSizePx,
    mmPerPx,
    tileSizeMm,
}: CheckerboardPatternInput): CheckerboardPatternPx {
    return {
        originPx: vec2.fromValues(
            crosshairCanvasPx[0],
            viewSizePx[1] - crosshairCanvasPx[1]
        ),
        tilePx: vec2.fromValues(
            tileSizeMm / mmPerPx[0],
            tileSizeMm / mmPerPx[1]
        ),
    };
}
